<template>
  <section class="admin-accounts">
    <p v-if="!users && !message" class="admin-note">{{ t('page-loading') }}</p>
    <template v-if="users">
      <div class="admin-search">
        <input v-model="search" type="search" name="accountSearch" :placeholder="t('admin-accounts-search')" :aria-label="t('admin-accounts-search')" />
      </div>
      <table v-if="shown.length">
        <tr><th>{{ t('admin-col-id') }}</th><th>{{ t('admin-col-name') }}</th><th>{{ t('admin-col-email') }}</th><th>{{ t('admin-col-games') }}</th><th></th><th></th></tr>
        <tr v-for="u in shown" :key="u.id" :data-user="u.id">
          <td class="num">{{ u.id }}</td>
          <td class="name">{{ u.displayName }}</td>
          <td class="mail">
            <span v-if="editing === u.id" class="inline">
              <input v-model="email" type="email" name="email" />
              <button type="button" @click="save(u)">{{ t('button-save') }}</button>
              <button type="button" @click="editing = null">{{ t('dialog-cancel') }}</button>
            </span>
            <template v-else>{{ u.email || t('account-no-email') }}</template>
          </td>
          <td class="glink"><button type="button" class="games-link" @click="$emit('show-games', u.id)">{{ t('admin-user-games', { n: gameCount(u) }) }}</button></td>
          <td class="status"><span v-if="u.disabled" class="pill off">{{ t('admin-user-disabled') }}</span> <span v-if="u.aiStudy" class="pill ai">{{ t('admin-user-ai') }}</span> <span v-if="u.aiGames" class="pill ai-games">{{ t('admin-user-ai-games') }}</span></td>
          <td class="acts">
            <span v-if="confirming && confirming.id === u.id" class="inline">
              {{ confirming.action === 'delete'
                ? t('admin-delete-user-confirm', { name: u.displayName })
                : t('admin-sign-out-user-confirm', { name: u.displayName }) }}
              <button type="button" :class="{ danger: confirming.action === 'delete' }" @click="confirmed(u)">{{ t('button-confirm') }}</button>
              <button type="button" @click="confirming = null">{{ t('dialog-cancel') }}</button>
            </span>
            <template v-else-if="editing !== u.id">
              <button type="button" @click="edit(u)">{{ t('admin-change-email') }}</button>
              <button type="button" @click="ask(u, 'sign-out')">{{ t('admin-sign-out-user') }}</button>
              <button type="button" @click="setDisabled(u, !u.disabled)">{{ u.disabled ? t('admin-enable-user') : t('admin-disable-user') }}</button>
              <button type="button" class="ai-study" @click="setAiStudy(u, !u.aiStudy)">{{ u.aiStudy ? t('admin-ai-off') : t('admin-ai-on') }}</button>
              <button type="button" class="ai-games" @click="setAiGames(u, !u.aiGames)">{{ u.aiGames ? t('admin-ai-games-off') : t('admin-ai-games-on') }}</button>
              <button type="button" class="danger" @click="ask(u, 'delete')">{{ t('admin-delete-user') }}</button>
            </template>
          </td>
        </tr>
      </table>
      <p v-else class="admin-note">{{ t('admin-no-match') }}</p>
    </template>
    <p v-if="message" :class="message.cls">{{ message.text }}</p>
  </section>
</template>

<script>
import { t, errorText } from '@/texts';
import { adminCall, onAdminChange, adminChanged } from '@/admin';

// The accounts tab: every account, narrowed by the search box, each with the number of games it
// plays in (from the games tab's list, `games`), which opens those games.
export default {
  name: 'AdminAccounts',
  props: { games: { type: Array, default: null } },
  data() {
    return { users: null, editing: null, confirming: null, email: '', message: null, search: '' };
  },
  computed: {
    shown() {
      const q = this.search.trim().toLowerCase();
      if (!this.users || !q) return this.users || [];
      return this.users.filter(u => String(u.id).includes(q) || (u.displayName || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q));
    },
  },
  watch: {
    users(list) { if (list) this.$emit('count', list.length); },
  },
  async created() {
    this.stop = onAdminChange(this.reload);
    await this.reload();
  },
  beforeDestroy() {
    this.stop();
  },
  methods: {
    t,
    gameCount(u) {
      return (this.games || []).filter(g => (g.players || []).some(p => p.id === u.id)).length;
    },
    async reload() {
      try {
        this.users = await adminCall('GET', 'users');
      } catch (e) {
        this.message = { cls: 'warn', text: t('load-failed', { reason: errorText(e.msg || String(e)) }) };
      }
    },
    changed() {
      adminChanged(this.reload);
    },
    edit(u) {
      this.message = null;
      this.editing = u.id;
      this.email = u.email || '';
    },
    ask(u, action) {
      this.message = null;
      this.editing = null;
      this.confirming = { id: u.id, action };
    },
    confirmed(u) {
      const action = this.confirming.action;
      this.confirming = null;
      if (action === 'delete') this.remove(u); else this.signOut(u);
    },
    async setDisabled(u, disabled) {
      this.message = null;
      try {
        const updated = await adminCall('PATCH', `users/${u.id}`, { disabled });
        u.disabled = !!updated.disabled;
        this.changed();
        this.message = { cls: 'info', text: t('admin-saved') };
      } catch (e) {
        this.message = { cls: 'warn', text: errorText(e.msg || String(e)) };
      }
    },
    // The AI in 试下 (SPEC §6.4); the account sees it at its next page load.
    async setAiStudy(u, aiStudy) {
      this.message = null;
      try {
        const updated = await adminCall('PATCH', `users/${u.id}`, { aiStudy });
        this.$set(u, 'aiStudy', !!updated.aiStudy);
        this.message = { cls: 'info', text: t('admin-saved') };
      } catch (e) {
        this.message = { cls: 'warn', text: errorText(e.msg || String(e)) };
      }
    },
    // Games against the AI (plan 2026-10-28-ai-games.md): the account may start them from its next
    // page load; its games in progress go on either way.
    async setAiGames(u, aiGames) {
      this.message = null;
      try {
        const updated = await adminCall('PATCH', `users/${u.id}`, { aiGames });
        this.$set(u, 'aiGames', !!updated.aiGames);
        this.message = { cls: 'info', text: t('admin-saved') };
      } catch (e) {
        this.message = { cls: 'warn', text: errorText(e.msg || String(e)) };
      }
    },
    // Only an account that plays in and created no game can be deleted (the server checks).
    async remove(u) {
      try {
        await adminCall('DELETE', `users/${u.id}`);
        this.users = this.users.filter(x => x.id !== u.id);
        this.changed();
        this.message = { cls: 'info', text: t('admin-saved') };
      } catch (e) {
        this.message = { cls: 'warn', text: errorText(e.msg || String(e)) };
      }
    },
    // Signs the account out on all its browsers (e.g. a WeChat browser with an old session).
    async signOut(u) {
      try {
        const sessions = await adminCall('GET', 'sessions', null, { user: u.id });
        for (const s of sessions) await adminCall('POST', `sessions/${s.id}/sign-out`, {});
        this.message = { cls: 'info', text: t('admin-signed-out') };
      } catch (e) {
        this.message = { cls: 'warn', text: errorText(e.msg || String(e)) };
      }
    },
    async save(u) {
      try {
        const updated = await adminCall('PATCH', `users/${u.id}`, { email: this.email });
        u.email = updated.email;
        this.editing = null;
        this.changed();
        this.message = { cls: 'info', text: t('admin-saved') };
      } catch (e) {
        this.message = { cls: 'warn', text: errorText(e.msg || String(e)) };
      }
    },
  },
};
</script>
